import { useEffect, useRef, useState } from "react";
import ErrorAlert from "../../../../design-system/primitives/components/ErrorAlert/ErrorAlert";
import Text from "../../../../design-system/primitives/components/Text/Text";
import { icons } from "../../../../design-system/primitives/icons";
import DefaultPortal from "../../../../design-system/utils/DefaultPortal/DefaultPortal.jsx";
import type { SparkChatMessage } from "../types";
import { debugOf } from "../types";
import {
  buildTranscript,
  describeReply,
  describeSent,
  describeTokens,
  tokenTotalsOf,
} from "./transcript";
import styles from "./SparkChatDebug.css";

const ToggleIcon = icons["chevron-right"];
const CopyIcon = icons["clipboard-copy"];

const COLLAPSED_KEY = "spark-chat-debug-collapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
};

const writeCollapsed = (value: boolean) => {
  try {
    localStorage.setItem(COLLAPSED_KEY, value ? "1" : "0");
  } catch {
    return;
  }
};
const ResetIcon = icons["rotate-ccw"];

type CopyStatus = "idle" | "copied" | "error";

const COPY_TITLE: Record<CopyStatus, string> = {
  idle: "Protokoll kopieren",
  copied: "Kopiert",
  error: "Kopieren fehlgeschlagen",
};

type SparkChatDebugProps = {
  sparkTitle: string;
  chatId: string;
  messages: SparkChatMessage[];
  busy: boolean;
  onReset: () => Promise<unknown>;
};

export const SparkChatDebug = ({
  sparkTitle,
  chatId,
  messages,
  busy,
  onReset,
}: SparkChatDebugProps) => {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [copyStatus, setCopyStatus] = useState<CopyStatus>("idle");
  const [resetting, setResetting] = useState(false);
  const [resetFailed, setResetFailed] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const copy = async () => {
    let next: CopyStatus;
    try {
      await navigator.clipboard.writeText(
        buildTranscript({ sparkTitle, chatId, messages, copiedAt: new Date() }),
      );
      next = "copied";
    } catch {
      next = "error";
    }
    clearTimeout(timeoutRef.current);
    setCopyStatus(next);
    timeoutRef.current = setTimeout(() => setCopyStatus("idle"), 2000);
  };

  const reset = async () => {
    setResetting(true);
    setResetFailed(false);
    try {
      // On success the panel is remounted with a new chat, this one included.
      await onReset();
    } catch {
      setResetFailed(true);
      setResetting(false);
    }
  };

  const lastReply = messages.findLast(
    (message) => message.role === "assistant" && debugOf(message),
  );

  return (
    <DefaultPortal>
      <aside
        className={
          collapsed ? `${styles.debug} ${styles.collapsed}` : styles.debug
        }
        aria-label="Testmodus"
      >
        <div className={styles.header}>
          <Text variant="subheader-small" as="h2">
            Testmodus
          </Text>
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={!collapsed}
            aria-label={
              collapsed ? "Testmodus aufklappen" : "Testmodus einklappen"
            }
            onClick={() => {
              writeCollapsed(!collapsed);
              setCollapsed(!collapsed);
            }}
          >
            <ToggleIcon
              size={20}
              aria-hidden
              className={collapsed ? undefined : styles.toggleIconOpen}
            />
          </button>
        </div>
        {!collapsed && (
          <>
            <Text variant="label-small">
              {`Nachrichten: ${describeSent(messages)}`}
            </Text>
            <Text variant="label-small">
              {`Tokens: ${describeTokens(tokenTotalsOf(messages))}`}
            </Text>
            {lastReply && (
              <Text variant="label-small">
                {`Letzte Antwort: ${describeReply(lastReply)}`}
              </Text>
            )}
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.action}
                disabled={messages.length === 0}
                onClick={() => void copy()}
              >
                <CopyIcon size={14} aria-hidden />
                {COPY_TITLE[copyStatus]}
              </button>
              <button
                type="button"
                className={styles.action}
                disabled={busy || resetting}
                onClick={() => void reset()}
              >
                <ResetIcon size={14} aria-hidden />
                Zurücksetzen
              </button>
            </div>
            {resetFailed && (
              <ErrorAlert title="Zurücksetzen hat nicht geklappt." />
            )}
          </>
        )}
      </aside>
    </DefaultPortal>
  );
};
